/**
 * Ce qu'un formulaire de contact doit vérifier avant de partir (#1308).
 *
 * Écrit à part et testé : ce sont des règles, et une règle trop stricte fait renoncer un prospect
 * qu'on ne reverra pas — tandis qu'une règle absente laisse arriver une demande sans moyen de
 * répondre.
 */

export type ContactFormValues = {
  senderName: string;
  companyName: string;
  senderEmail: string;
  phone: string;
  /** Nombre de consultants portés, tel qu'il est saisi. Facultatif. */
  portedCount: string;
  subject: string;
  message: string;
  /**
   * Champ leurre : invisible pour un humain, tentant pour un robot.
   *
   * **Envoyé tel quel** — c'est le serveur qui tranche, et il rend alors la même réponse qu'un
   * envoi réussi. Le bloquer ici aussi donnerait deux règles pour une même protection.
   */
  website: string;
};

export const EMPTY_CONTACT_FORM: ContactFormValues = {
  senderName: '',
  companyName: '',
  senderEmail: '',
  phone: '',
  portedCount: '',
  subject: '',
  message: '',
  website: '',
};

export type ContactFieldError = keyof ContactFormValues;

/**
 * L'adresse est **le seul canal de réponse** : c'est le seul champ dont la forme est vérifiée.
 *
 * Vérification volontairement minimale — une arobase entourée de texte, un point après. Les
 * expressions plus savantes rejettent des adresses valides, et refuser l'adresse d'un prospect
 * sur un motif qu'il ne comprend pas le fait partir.
 */
const looksLikeEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/**
 * Les champs manquants ou mal formés, dans l'ordre où ils apparaissent à l'écran.
 *
 * Le nombre de portés n'est **pas** requis : un prospect qui ne le connaît pas encore doit pouvoir
 * écrire. S'il est saisi, il doit être un entier positif — « une quinzaine » ne se compare à aucun
 * palier.
 */
export function validateContactForm(values: ContactFormValues): ContactFieldError[] {
  const errors: ContactFieldError[] = [];

  if (!values.senderName.trim()) errors.push('senderName');
  if (!values.companyName.trim()) errors.push('companyName');
  if (!looksLikeEmail(values.senderEmail)) errors.push('senderEmail');

  const ported = values.portedCount.trim();
  if (ported && !/^\d{1,5}$/.test(ported)) errors.push('portedCount');

  if (!values.subject.trim()) errors.push('subject');
  if (!values.message.trim()) errors.push('message');

  return errors;
}

/** Corps envoyé à `POST /public/support/contact`. */
export type ContactPayload = {
  senderName: string;
  /** Le leurre, transmis vide dans le cas normal. */
  website?: string;
  companyName: string;
  senderEmail: string;
  phone?: string;
  portedCount?: number;
  subject: string;
  message: string;
};

/**
 * La saisie, mise en forme pour l'API.
 *
 * Les champs facultatifs vides sont **omis**, jamais envoyés vides : la validation refuse le corps
 * entier sur un champ hors contrat, et une chaîne vide n'est pas une absence — un téléphone vide
 * enregistré comme tel se lit ensuite « pas de téléphone renseigné », ce qui est vrai, mais un
 * `portedCount: ''` ne se lit pas du tout.
 */
export function contactPayload(values: ContactFormValues): ContactPayload {
  const ported = values.portedCount.trim();
  const phone = values.phone.trim();

  return {
    senderName: values.senderName.trim(),
    companyName: values.companyName.trim(),
    senderEmail: values.senderEmail.trim(),
    subject: values.subject.trim(),
    message: values.message.trim(),
    ...(phone ? { phone } : {}),
    ...(ported ? { portedCount: Number(ported) } : {}),
    ...(values.website.trim() ? { website: values.website.trim() } : {}),
  };
}

/**
 * Le champ leurre est-il rempli ?
 *
 * Gardée pour les tests, mais **plus utilisée à l'envoi** : le serveur seul tranche, et il rend la
 * même réponse qu'un envoi réussi. Deux règles pour une même protection finissent par diverger.
 */
export const looksAutomated = (values: ContactFormValues): boolean => values.website.trim() !== '';
